import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, useLanguage, apiClient } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import PageHeader from "../components/layout/PageHeader";
import { toast } from "sonner";
import {
  Bell, Clock, Volume2, Moon, Plus, Edit2, Trash2, Play,
  Loader2, X, Check
} from "lucide-react";

const DAY_OPTIONS = [
  { value: "mon", label: "Sen", labelEn: "Mon" },
  { value: "tue", label: "Sel", labelEn: "Tue" },
  { value: "wed", label: "Rab", labelEn: "Wed" },
  { value: "thu", label: "Kam", labelEn: "Thu" },
  { value: "fri", label: "Jum", labelEn: "Fri" },
  { value: "sat", label: "Sab", labelEn: "Sat" },
  { value: "sun", label: "Min", labelEn: "Sun" },
];

const SOUND_OPTIONS = [
  { value: "default", label: "Default", labelId: "Standar" },
  { value: "gentle", label: "Gentle", labelId: "Lembut" },
  { value: "energetic", label: "Energetic", labelId: "Semangat" },
  { value: "spiritual", label: "Spiritual", labelId: "Spiritual" },
  { value: "silent", label: "Silent", labelId: "Hening" },
];

const PRAYER_TIME_OPTIONS = [
  { value: "none", label: "Manual", labelId: "Manual" },
  { value: "Imsak", label: "Imsak", labelId: "Imsak" },
  { value: "Fajr", label: "Fajr", labelId: "Subuh" },
  { value: "Dhuhr", label: "Dhuhr", labelId: "Zuhur" },
  { value: "Asr", label: "Asr", labelId: "Ashar" },
  { value: "Maghrib", label: "Maghrib", labelId: "Maghrib" },
  { value: "Isha", label: "Isha", labelId: "Isya" },
];

const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

const Reminders = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { language } = useLanguage();
  const isEnglish = language === "en";

  // Reminders state
  const [reminders, setReminders] = useState([]);
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const prefillAppliedRef = useRef(false);

  // Form state
  const [formData, setFormData] = useState({
    habit_id: null,
    title: "",
    message: "",
    reminder_time: "08:00",
    frequency: "daily",
    days: [],
    sound: "default",
    prayer_time_anchor: "none",
    prayer_time_offset: 0,
  });

  // Fetch reminders and settings
  useEffect(() => {
    fetchReminders();
    fetchSettings();
  }, []);

  useEffect(() => {
    const prefill = location.state?.prefillReminder;
    if (!prefill || prefillAppliedRef.current) return;

    prefillAppliedRef.current = true;
    setEditingReminder(null);
    setFormData({
      habit_id: null,
      title: prefill.title || "",
      message: prefill.message || "",
      reminder_time: prefill.reminder_time || "08:00",
      frequency: prefill.frequency || "daily",
      days: prefill.days || [],
      sound: prefill.sound || settings?.default_sound || "default",
      prayer_time_anchor: prefill.prayer_time_anchor || "none",
      prayer_time_offset: prefill.prayer_time_offset || 0,
    });
    setShowReminderDialog(true);
  }, [location.state, settings]);

  const fetchReminders = async () => {
    try {
      const response = await apiClient.get("/reminders");
      setReminders(response.data.reminders || []);
    } catch (error) {
      console.error("Error fetching reminders:", error);
      toast.error(isEnglish ? "Failed to load reminders" : "Gagal memuat pengingat");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await apiClient.get("/reminder-settings");
      setSettings(response.data.settings);
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const normalizeReminderTime = (timeValue, fallback) => {
    if (!timeValue) return fallback;
    const text = String(timeValue);
    if (text.length >= 5 && text[2] === ":") return text.slice(0, 5);
    return fallback;
  };

  const clampMinutes = (value) => {
    if (Number.isNaN(value)) return 0;
    return Math.min(120, Math.max(-120, value));
  };

  const addMinutesToTime = (timeValue, offsetMinutes) => {
    if (!timeValue) return null;
    const [h, m] = timeValue.slice(0, 5).split(":").map((x) => parseInt(x, 10));
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    const total = (h * 60 + m + offsetMinutes + 1440) % 1440;
    const hh = String(Math.floor(total / 60)).padStart(2, "0");
    const mm = String(total % 60).padStart(2, "0");
    return `${hh}:${mm}`;
  };

  const getLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation is not supported by your browser"));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => reject(error),
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    });
  };

  const applyPrayerTime = async (anchor, offsetOverride) => {
    if (!anchor || anchor === "none") {
      setFormData((prev) => ({ ...prev, prayer_time_anchor: "none", prayer_time_offset: 0 }));
      return;
    }

    try {
      const loc = await getLocation();
      const res = await apiClient.get("/prayer-times", {
        params: { latitude: loc.latitude, longitude: loc.longitude },
      });
      const timings = res.data?.timings || {};
      const timeValue = timings[anchor];
      const normalizedTime = normalizeReminderTime(timeValue, formData.reminder_time);
      const offsetMinutes = clampMinutes(
        offsetOverride !== undefined ? offsetOverride : formData.prayer_time_offset
      );
      const reminderTime = addMinutesToTime(normalizedTime, offsetMinutes) || normalizedTime;
      setFormData((prev) => ({
        ...prev,
        prayer_time_anchor: anchor,
        prayer_time_offset: offsetMinutes,
        reminder_time: reminderTime,
      }));
    } catch (error) {
      toast.error(isEnglish ? "Failed to get prayer time" : "Gagal mengambil waktu shalat");
      setFormData((prev) => ({ ...prev, prayer_time_anchor: "none", prayer_time_offset: 0 }));
    }
  };

  const handleOffsetChange = (value) => {
    const offsetMinutes = clampMinutes(value);
    setFormData((prev) => ({
      ...prev,
      prayer_time_offset: offsetMinutes,
    }));
    if (formData.prayer_time_anchor && formData.prayer_time_anchor !== "none") {
      applyPrayerTime(formData.prayer_time_anchor, offsetMinutes);
    }
  };

  const registerPushNotifications = async () => {
    if (!("Notification" in window)) {
      toast.error(isEnglish ? "Notifications are not supported" : "Notifikasi tidak didukung");
      return;
    }
    if (!("serviceWorker" in navigator)) {
      toast.error(isEnglish ? "Service worker not supported" : "Service worker tidak didukung");
      return;
    }
    try {
      const permission = await Notification.requestPermission();
      if (permission === "denied") {
        toast.error(isEnglish ? "Permission blocked" : "Izin diblokir");
        return;
      }
      if (permission !== "granted") {
        toast.message(isEnglish ? "Permission not set" : "Izin belum diatur");
        return;
      }
      const registration = await navigator.serviceWorker.register("/service-worker.js");
      const keyResponse = await apiClient.get("/push/public-key");
      const publicKey = keyResponse.data?.public_key;
      if (!publicKey) {
        toast.error(isEnglish ? "Missing push key" : "Kunci push tidak tersedia");
        return;
      }
      const applicationServerKey = urlBase64ToUint8Array(publicKey);
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey
        });
      }
      const payload = subscription.toJSON ? subscription.toJSON() : subscription;
      await apiClient.post("/push/subscribe", payload);
      toast.success(isEnglish ? "Notifications enabled" : "Notifikasi diaktifkan");
    } catch (error) {
      console.error("Error registering push notifications:", error);
      const message = error.response?.data?.detail || (isEnglish ? "Failed to enable notifications" : "Gagal mengaktifkan notifikasi");
      toast.error(message);
    }
  };

  const handleRequestNotificationPermission = async () => {
    await registerPushNotifications();
  };

  const handleCreateReminder = () => {
    setEditingReminder(null);
    setFormData({
      habit_id: null,
      title: "",
      message: "",
      reminder_time: "08:00",
      frequency: "daily",
      days: [],
      sound: settings?.default_sound || "default",
      prayer_time_anchor: "none",
      prayer_time_offset: 0,
    });
    setShowReminderDialog(true);
  };

  const handleEditReminder = (reminder) => {
    setEditingReminder(reminder);
    setFormData({
      habit_id: reminder.habit_id,
      title: reminder.title,
      message: reminder.message,
      reminder_time: reminder.reminder_time,
      frequency: reminder.frequency,
      days: reminder.days || [],
      sound: reminder.sound,
      prayer_time_anchor: reminder.prayer_time_anchor || "none",
      prayer_time_offset: reminder.prayer_time_offset || 0,
    });
    setShowReminderDialog(true);
  };

  const handleDeleteReminder = async (reminderId) => {
    if (!confirm(isEnglish ? "Are you sure you want to delete this reminder?" : "Yakin ingin menghapus pengingat ini?")) {
      return;
    }

    try {
      await apiClient.delete(`/reminders/${reminderId}`);
      toast.success(isEnglish ? "Reminder deleted" : "Pengingat dihapus");
      fetchReminders();
    } catch (error) {
      console.error("Error deleting reminder:", error);
      toast.error(isEnglish ? "Failed to delete reminder" : "Gagal menghapus pengingat");
    }
  };

  const handleToggleReminder = async (reminder) => {
    try {
      await apiClient.put(`/reminders/${reminder.reminder_id}`, {
        enabled: !reminder.enabled,
      });
      toast.success(isEnglish ? "Reminder updated" : "Pengingat diperbarui");
      fetchReminders();
    } catch (error) {
      console.error("Error toggling reminder:", error);
      toast.error(isEnglish ? "Failed to update reminder" : "Gagal memperbarui pengingat");
    }
  };

  const handleTestReminder = async (reminderId) => {
    try {
      const response = await apiClient.post(`/reminders/${reminderId}/test`);
      toast.success(isEnglish ? "Test notification sent" : "Notifikasi tes dikirim");
    } catch (error) {
      console.error("Error testing reminder:", error);
      toast.error(isEnglish ? "Failed to send test notification" : "Gagal mengirim notifikasi tes");
    }
  };

  const handleSaveReminder = async () => {
    try {
      setIsSaving(true);

      // Validate
      if (!formData.title.trim()) {
        toast.error(isEnglish ? "Title is required" : "Judul harus diisi");
        return;
      }

      if (!formData.message.trim()) {
        toast.error(isEnglish ? "Message is required" : "Pesan harus diisi");
        return;
      }

      if (formData.frequency === "specific_days" && formData.days.length === 0) {
        toast.error(isEnglish ? "Select at least one day" : "Pilih minimal satu hari");
        return;
      }

      const payload = {
        habit_id: formData.habit_id,
        title: formData.title,
        message: formData.message,
        reminder_time: formData.reminder_time,
        frequency: formData.frequency,
        sound: formData.sound,
        days: formData.frequency === "specific_days" ? formData.days : [],
        prayer_time_anchor: formData.prayer_time_anchor,
        prayer_time_offset: formData.prayer_time_offset,
      };

      if (editingReminder) {
        await apiClient.put(`/reminders/${editingReminder.reminder_id}`, payload);
        toast.success(isEnglish ? "Reminder updated" : "Pengingat diperbarui");
      } else {
        await apiClient.post("/reminders", payload);
        toast.success(isEnglish ? "Reminder created" : "Pengingat dibuat");
      }

      setShowReminderDialog(false);
      fetchReminders();
    } catch (error) {
      console.error("Error saving reminder:", error);
      const errorMsg = error.response?.data?.detail || (isEnglish ? "Failed to save reminder" : "Gagal menyimpan pengingat");
      toast.error(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateSettings = async (field, value) => {
    try {
      const newSettings = { ...settings, [field]: value };
      setSettings(newSettings);

      await apiClient.put("/reminder-settings", { [field]: value });
      toast.success(isEnglish ? "Settings updated" : "Pengaturan diperbarui");
    } catch (error) {
      console.error("Error updating settings:", error);
      toast.error(isEnglish ? "Failed to update settings" : "Gagal memperbarui pengaturan");
      // Revert on error
      setSettings(settings);
    }
  };

  const toggleDay = (dayValue) => {
    setFormData((prev) => ({
      ...prev,
      days: prev.days.includes(dayValue)
        ? prev.days.filter((d) => d !== dayValue)
        : [...prev.days, dayValue],
    }));
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getFrequencyText = (reminder) => {
    if (reminder.frequency === "daily") {
      return isEnglish ? "Every day" : "Setiap hari";
    } else if (reminder.frequency === "weekly") {
      return isEnglish ? "Every week" : "Setiap minggu";
    } else if (reminder.frequency === "specific_days") {
      if (!reminder.days || reminder.days.length === 0) return "-";
      const dayNames = reminder.days.map((d) => {
        const day = DAY_OPTIONS.find((opt) => opt.value === d);
        return isEnglish ? day.labelEn : day.label;
      });
      return dayNames.join(", ");
    }
    return "-";
  };

  const getPrayerTimeLabel = (anchor) => {
    const found = PRAYER_TIME_OPTIONS.find((option) => option.value === anchor);
    if (!found) return anchor;
    return isEnglish ? found.label : found.labelId;
  };

  const getPrayerTimeBadge = (reminder) => {
    const anchor = reminder.prayer_time_anchor;
    if (!anchor || anchor === "none") return null;
    const label = getPrayerTimeLabel(anchor);
    const offset = reminder.prayer_time_offset || 0;
    const offsetText = offset === 0 ? "" : ` ${offset > 0 ? "+" : ""}${offset}m`;
    return isEnglish ? `Prayer: ${label}${offsetText}` : `Shalat: ${label}${offsetText}`;
  };

  const isPrayerAnchorActive = formData.prayer_time_anchor && formData.prayer_time_anchor !== "none";

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="reminders-page">
      <PageHeader title={isEnglish ? "Reminders" : "Pengingat"} />
      <div className="page-container space-y-6">
        {/* Reminder Settings */}
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Bell className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold">{isEnglish ? "Notification Settings" : "Pengaturan Notifikasi"}</h3>
                <p className="text-sm text-muted-foreground">{isEnglish ? "Configure how you receive reminders" : "Atur cara Anda menerima pengingat"}</p>
              </div>
              <Button variant="outline" size="sm" onClick={handleRequestNotificationPermission}>
                {isEnglish ? "Request Permission" : "Minta izin"}
              </Button>
            </div>

            <div className="space-y-4">
              {/* Enable Notifications */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{isEnglish ? "Enable Notifications" : "Aktifkan Notifikasi"}</p>
                  <p className="text-sm text-muted-foreground">{isEnglish ? "Receive reminder notifications" : "Terima notifikasi pengingat"}</p>
                </div>
                <Switch
                  checked={settings?.notification_enabled ?? true}
                  onCheckedChange={(val) => handleUpdateSettings("notification_enabled", val)}
                />
              </div>

              {/* Default Sound */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{isEnglish ? "Default Sound" : "Suara Default"}</p>
                  <p className="text-sm text-muted-foreground">{isEnglish ? "Notification sound" : "Suara notifikasi"}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Volume2 className="w-4 h-4 text-muted-foreground" />
                  <Select
                    value={settings?.default_sound || "default"}
                    onValueChange={(val) => handleUpdateSettings("default_sound", val)}
                  >
                    <SelectTrigger className="w-36">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SOUND_OPTIONS.map((sound) => (
                        <SelectItem key={sound.value} value={sound.value}>
                          {isEnglish ? sound.label : sound.labelId}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Quiet Hours */}
              <div className="space-y-3 pt-3 border-t">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{isEnglish ? "Quiet Hours" : "Jam Tenang"}</p>
                    <p className="text-sm text-muted-foreground">{isEnglish ? "Disable notifications during specific hours" : "Nonaktifkan notifikasi pada jam tertentu"}</p>
                  </div>
                  <Switch
                    checked={settings?.quiet_hours_enabled ?? false}
                    onCheckedChange={(val) => handleUpdateSettings("quiet_hours_enabled", val)}
                  />
                </div>

                {settings?.quiet_hours_enabled && (
                  <div className="flex items-center gap-3 pl-4">
                    <Moon className="w-4 h-4 text-muted-foreground" />
                    <Input
                      type="time"
                      value={settings?.quiet_hours_start || "22:00"}
                      onChange={(e) => handleUpdateSettings("quiet_hours_start", e.target.value)}
                      className="w-32"
                    />
                    <span className="text-muted-foreground">-</span>
                    <Input
                      type="time"
                      value={settings?.quiet_hours_end || "06:00"}
                      onChange={(e) => handleUpdateSettings("quiet_hours_end", e.target.value)}
                      className="w-32"
                    />
                  </div>
                )}
              </div>

              {/* Snooze Duration */}
              <div className="flex items-center justify-between pt-3 border-t">
                <div>
                  <p className="font-medium">{isEnglish ? "Snooze Duration" : "Durasi Tidur"}</p>
                  <p className="text-sm text-muted-foreground">{isEnglish ? "Default snooze time" : "Waktu tidur default"}</p>
                </div>
                <Select
                  value={String(settings?.snooze_duration || 5)}
                  onValueChange={(val) => handleUpdateSettings("snooze_duration", parseInt(val))}
                >
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 min</SelectItem>
                    <SelectItem value="10">10 min</SelectItem>
                    <SelectItem value="15">15 min</SelectItem>
                    <SelectItem value="30">30 min</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Reminders List */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">{isEnglish ? "My Reminders" : "Pengingat Saya"}</h2>
          <Button onClick={handleCreateReminder} size="sm">
            <Plus className="w-4 h-4 mr-1" />
            {isEnglish ? "Add Reminder" : "Tambah Pengingat"}
          </Button>
        </div>

        {reminders.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-2">{isEnglish ? "No reminders yet" : "Belum ada pengingat"}</p>
              <p className="text-sm text-muted-foreground mb-4">{isEnglish ? "Create your first reminder to stay on track" : "Buat pengingat pertama Anda untuk tetap on track"}</p>
              <Button onClick={handleCreateReminder}>
                <Plus className="w-4 h-4 mr-2" />
                {isEnglish ? "Create Reminder" : "Buat Pengingat"}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {reminders.map((reminder) => {
              const prayerBadge = getPrayerTimeBadge(reminder);
              return (
                <Card key={reminder.reminder_id} className={!reminder.enabled ? "opacity-60" : ""}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                        <Clock className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold truncate">{reminder.title}</h4>
                            <p className="text-sm text-muted-foreground truncate">{reminder.message}</p>
                          </div>
                          <Switch
                            checked={reminder.enabled}
                            onCheckedChange={() => handleToggleReminder(reminder)}
                            className="shrink-0"
                          />
                        </div>
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                          <Badge variant="secondary" className="font-medium">
                            {formatTime(reminder.reminder_time)}
                          </Badge>
                          <span className="text-muted-foreground text-xs">{getFrequencyText(reminder)}</span>
                          {reminder.habit_name && (
                            <Badge variant="outline" className="text-xs">
                              {reminder.habit_name}
                            </Badge>
                          )}
                          <Badge variant="outline" className="text-xs">
                            {isEnglish ? SOUND_OPTIONS.find(s => s.value === reminder.sound)?.label : SOUND_OPTIONS.find(s => s.value === reminder.sound)?.labelId}
                          </Badge>
                          {prayerBadge && (
                            <Badge variant="outline" className="text-xs">
                              {prayerBadge}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleTestReminder(reminder.reminder_id)}
                          className="h-8 w-8"
                          title={isEnglish ? "Test notification" : "Tes notifikasi"}
                        >
                          <Play className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditReminder(reminder)}
                          className="h-8 w-8"
                          title={isEnglish ? "Edit" : "Ubah"}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteReminder(reminder.reminder_id)}
                          className="h-8 w-8 text-destructive"
                          title={isEnglish ? "Delete" : "Hapus"}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Reminder Dialog */}
      <Dialog open={showReminderDialog} onOpenChange={setShowReminderDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingReminder ? (isEnglish ? "Edit Reminder" : "Ubah Pengingat") : (isEnglish ? "Create Reminder" : "Buat Pengingat")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">{isEnglish ? "Title" : "Judul"} *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder={isEnglish ? "e.g., Morning Prayer" : "Contoh: Sholat Pagi"}
              />
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label htmlFor="message">{isEnglish ? "Message" : "Pesan"} *</Label>
              <Input
                id="message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder={isEnglish ? "e.g., Don't forget to pray Fajr" : "Contoh: Jangan lupa sholat Subuh"}
              />
            </div>

            {/* Time */}
            <div className="space-y-2">
              <Label htmlFor="time">{isEnglish ? "Reminder Time" : "Waktu Pengingat"} *</Label>
              <Input
                id="time"
                type="time"
                value={formData.reminder_time}
                onChange={(e) => setFormData({ ...formData, reminder_time: e.target.value, prayer_time_anchor: "none", prayer_time_offset: 0 })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="prayerTime">
                {isEnglish ? "Use prayer time" : "Gunakan waktu shalat"}
              </Label>
              <Select
                value={formData.prayer_time_anchor || "none"}
                onValueChange={(val) => applyPrayerTime(val)}
              >
                <SelectTrigger id="prayerTime">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRAYER_TIME_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {isEnglish ? option.label : option.labelId}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {isEnglish
                  ? "Uses your location to match local prayer times."
                  : "Menggunakan lokasi untuk mengikuti jadwal shalat setempat."}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="offset">
                {isEnglish ? "Offset minutes" : "Offset menit"}
              </Label>
              <Input
                id="offset"
                type="number"
                min={-120}
                max={120}
                value={formData.prayer_time_offset}
                onChange={(e) => handleOffsetChange(parseInt(e.target.value, 10))}
                placeholder={isEnglish ? "e.g., 10 or -5" : "Contoh: 10 atau -5"}
                disabled={!isPrayerAnchorActive}
              />
              <div className="flex flex-wrap gap-2">
                {[-10, -5, 5, 10].map((value) => (
                  <Button
                    key={value}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleOffsetChange(value)}
                    disabled={!isPrayerAnchorActive}
                  >
                    {value > 0 ? `+${value}m` : `${value}m`}
                  </Button>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleOffsetChange(0)}
                  disabled={!isPrayerAnchorActive}
                >
                  {isEnglish ? "Reset" : "Reset"}
                </Button>
              </div>
            </div>

            {/* Frequency */}
            <div className="space-y-2">
              <Label htmlFor="frequency">{isEnglish ? "Frequency" : "Frekuensi"}</Label>
              <Select
                value={formData.frequency}
                onValueChange={(val) => setFormData({ ...formData, frequency: val, days: [] })}
              >
                <SelectTrigger id="frequency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">{isEnglish ? "Every day" : "Setiap hari"}</SelectItem>
                  <SelectItem value="weekly">{isEnglish ? "Every week" : "Setiap minggu"}</SelectItem>
                  <SelectItem value="specific_days">{isEnglish ? "Specific days" : "Hari tertentu"}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Days Selection */}
            {formData.frequency === "specific_days" && (
              <div className="space-y-2">
                <Label>{isEnglish ? "Select Days" : "Pilih Hari"}</Label>
                <div className="flex flex-wrap gap-2">
                  {DAY_OPTIONS.map((day) => {
                    const isSelected = formData.days.includes(day.value);
                    return (
                      <button
                        key={day.value}
                        type="button"
                        onClick={() => toggleDay(day.value)}
                        className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${
                          isSelected
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted text-muted-foreground hover:bg-muted-foreground/10"
                        }`}
                      >
                        {isEnglish ? day.labelEn : day.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Sound */}
            <div className="space-y-2">
              <Label htmlFor="sound">{isEnglish ? "Notification Sound" : "Suara Notifikasi"}</Label>
              <Select
                value={formData.sound}
                onValueChange={(val) => setFormData({ ...formData, sound: val })}
              >
                <SelectTrigger id="sound">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SOUND_OPTIONS.map((sound) => (
                    <SelectItem key={sound.value} value={sound.value}>
                      <div className="flex items-center gap-2">
                        <Volume2 className="w-4 h-4" />
                        {isEnglish ? sound.label : sound.labelId}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowReminderDialog(false)}
              disabled={isSaving}
            >
              {isEnglish ? "Cancel" : "Batal"}
            </Button>
            <Button onClick={handleSaveReminder} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isEnglish ? "Saving..." : "Menyimpan..."}
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  {isEnglish ? "Save" : "Simpan"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Reminders;
